import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { animate } from "motion/react";
import { triggerOpenChatbot } from "@/hooks/useChatbotTrigger";

function CountUp({ to, duration = 2, suffix = "" }: { to: number; duration?: number; suffix?: string }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(0, to, {
      duration,
      ease: [0.25, 0.1, 0.25, 1] as const,
      onUpdate(value) {
        setDisplay(Math.round(value));
      },
    });
    return () => controls.stop();
  }, [to, duration]);

  return <>{display.toLocaleString()}{suffix}</>;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const statsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 1.0,
    },
  },
};

const statsItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

export default function HeroSection() {
  const [statsInView, setStatsInView] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStatsInView(true), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative w-full h-[520px] md:h-[680px] flex items-center justify-center overflow-hidden">
      {/* Background Image with parallax-like zoom */}
      <div className="absolute inset-0">
        <motion.img decoding="async"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.25, 0.1, 0.25, 1] as const }}
          src="https://img.echotwin.kr/kangstudy/3a962cbcf9d45826.jpg"
          alt="강스터디 경매 부동산 교육 플랫폼"
          className="w-full h-full object-cover object-top"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"
        ></motion.div>
      </div>

      {/* Floating accent dots */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute top-[15%] left-[10%] w-2 h-2 rounded-full bg-[#C4956A]/40 blur-[1px] hidden md:block"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute top-[25%] right-[15%] w-1.5 h-1.5 rounded-full bg-white/30 blur-[1px] hidden md:block"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 2.1, duration: 1 }}
        className="absolute bottom-[20%] left-[20%] w-1 h-1 rounded-full bg-[#C4956A]/30 blur-[1px] hidden md:block"
      />

      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full px-4 md:px-6 text-center max-w-[900px] mx-auto"
      >
        {/* Badge */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
          whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <motion.span
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ delay: 1.2, duration: 1.2, ease: "easeInOut" as const }}
          >
            <i className="ri-sparkling-line text-[#C4956A]"></i>
          </motion.span>
          <span className="text-sm text-white/90">
            신규 회원가입 시 무료 입문강의 100% 제공
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          variants={itemVariants}
          className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-5"
        >
          초보도 따라오는
          <br />
          실전 경매 공부,{" "}
          <motion.span
            className="text-[#C4956A] inline-block"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 0.6, ease: "easeOut" as const }}
          >
            강스터디
          </motion.span>
        </motion.h1>

        {/* Description */}
        <motion.p
          variants={itemVariants}
          className="text-base md:text-lg text-white/80 mb-8 max-w-[600px] mx-auto leading-relaxed"
        >
          경매 물건 분석부터 교육, 상담, 실전 투자 준비까지
          <br className="hidden md:block" />
          한 곳에서 시작하세요.
        </motion.p>

        {/* Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/lectures?category=beginner"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-[#1C1917] font-semibold rounded-full hover:bg-[#C4956A] hover:text-white transition-all text-sm whitespace-nowrap text-center inline-block"
            >
              <i className="ri-play-circle-line mr-2"></i>
              무료강의 보기
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/courses"
              className="w-full sm:w-auto px-8 py-3.5 bg-[#C4956A] text-white font-semibold rounded-full hover:bg-[#B08050] transition-all text-sm whitespace-nowrap text-center inline-block"
            >
              <i className="ri-book-open-line mr-2"></i>
              교육 신청하기
            </Link>
          </motion.div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          variants={statsContainerVariants}
          initial="hidden"
          animate={statsInView ? "visible" : "hidden"}
          className="flex items-center justify-center gap-8 md:gap-12 mt-12"
        >
          <motion.div variants={statsItemVariants} className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-white">
              <CountUp to={12000} duration={2.2} suffix="+" />
            </p>
            <p className="text-xs md:text-sm text-white/60 mt-1">누적 수강생</p>
          </motion.div>
          <motion.div variants={statsItemVariants} className="w-px h-10 bg-white/20"></motion.div>
          <motion.div variants={statsItemVariants} className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-white">
              <CountUp to={500} duration={2} suffix="+" />
            </p>
            <p className="text-xs md:text-sm text-white/60 mt-1">분석 리포트</p>
          </motion.div>
          <motion.div variants={statsItemVariants} className="w-px h-10 bg-white/20"></motion.div>
          <motion.div variants={statsItemVariants} className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-white">
              <CountUp to={98} duration={1.8} suffix="%" />
            </p>
            <p className="text-xs md:text-sm text-white/60 mt-1">수강 만족도</p>
          </motion.div>
        </motion.div>

        {/* Floating AI Chatbot Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0, duration: 0.7, ease: [0.25, 0.1, 0.25, 1] as const }}
          className="mt-14"
        >
          <motion.button
            onClick={triggerOpenChatbot}
            className="group relative inline-flex items-center gap-3 px-6 py-3.5 bg-white/10 backdrop-blur-md border-2 border-white/25 rounded-full hover:bg-white/20 hover:border-white/40 transition-all cursor-pointer"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {/* pulsing ring */}
            <span className="absolute inset-0 rounded-full bg-white/5 animate-ping duration-[2500ms]" style={{ animationDelay: '0.5s' }}></span>

            {/* icon container with glow */}
            <span className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#C4956A] to-[#E8B88A] shadow-lg shadow-[#C4956A]/30 group-hover:shadow-[#C4956A]/50 transition-shadow">
              <motion.i
                className="ri-robot-2-line text-white text-lg"
                animate={{ rotate: [0, 8, -8, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" as const, delay: 2.5 }}
              ></motion.i>
            </span>

            {/* text */}
            <span className="relative text-white font-semibold text-sm md:text-base whitespace-nowrap">
              AI 경매 비서에게 물어보기
            </span>

            {/* arrow */}
            <motion.i
              className="ri-arrow-right-line text-white/70 text-sm group-hover:text-white group-hover:translate-x-1 transition-all hidden sm:block"
            ></motion.i>

            {/* sparkle accent */}
            <motion.i
              className="ri-sparkling-2-line text-[#C4956A] text-xs absolute -top-1.5 -right-1.5"
              animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" as const }}
            ></motion.i>
          </motion.button>

          <p className="text-white/50 text-xs mt-3">
            경매 초보도 OK · 무료로 질문하세요
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}